<template functional>
  <!--
    汎用的なテキストボックスコンポーネント
    v-model に対応
    input タグに設定できるイベントは全て設定可能
    オートコンプリートにも対応し、@autocomplete で選択時のハンドリングができる
  -->
  <div
    :class="['TkTextBox', { '-disabled': props.disabled }, data.staticClass]"
    :style="[
      $options.styleVars(props),
      props.width ? { width: `${props.width}px` } : {},
    ]"
    v-bind="(() => {
      // 親のスコープIDを設定しルート要素だけ親の scoped css を反映できるようにする
      data.attrs[parent.$options._scopeId] = ''
      return data.attrs
    })()"
  >
    <input
      :value="props.value"
      type="text"
      :class="[
        'tkTextBox_input',
        `-${props.theme}`,
        { [`-${props.color}`]: !!props.color },
        { '-putIconOutside': props.putIconOutside },
        { '-noBorder': props.noBorder },
        { '-bold': props.bold },
        { '-active': props.active },
      ]"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :readonly="props.readonly"
      v-on="{
        ...listeners,
        // 基本的にはイベントハンドラーは全て <input> タグに流すが、@input イベントだけは v-model の仕様に合わせる
        ...(listeners.input ? { input: $event => listeners.input($event.target.value) } : {}),
      }"
    >
    <component
      :is="$options.components.TkIcon"
      v-if="props.icon"
      :class="[
        ' tkTextBox_icon',
        { '-putIconOutside': props.putIconOutside },
      ]"
      :name="props.icon"
      :color="props.iconColor"
    />
    <div
      v-if="props.iconSeparator"
      :class="[
        'tkTextBox_separator',
        `-${props.iconSeparator}`,
      ]"
    />
    <ul
      v-if="props.autocompleteItems.length > 0"
      :class="[
        'tkTextBox_autocomplete',
        `-${props.theme}`,
        `-${props.size}`,
        { '-putIconOutside': props.putIconOutside },
      ]"
      :style="{
        'max-height': `${props.maxAutocompleteBoxHeight}px`,
      }"
    >
      <li
        v-for="(item, index) in props.autocompleteItems"
        :key="item.value"
        :class="[
          'tkTextBox_autocompleteItem',
          `-${props.theme}`,
          `-${props.size}`,
          index < props.autocompleteItems.length - 1 ? '-border' : null,
        ]"
        @click="listeners.autocomplete ? listeners.autocomplete(item.value) : null"
      >
        {{ item.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import TkIcon from '../TkIcon/TkIcon.vue'
import { input } from '../../../mixins'

export default {
  name: 'TkTextBox',

  components: { TkIcon },

  props: {
    // ---------------
    // 機能に関するもの

    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },

    // ---------------
    // 外観に関するもの

    theme: {
      type: String,
      default: 'default', // default, flat が指定可能
    },
    // 文字サイズと高さのプリセット
    // 'normal', 'large', 'x-large', 'xx-large' 'xxx-large' が指定可能
    // 高さはそれぞれ 30, 36, 40, 45, 50
    size: {
      type: String,
      default: 'normal',
    },
    width: {
      type: Number,
      default: undefined,
    },
    color: {
      type: String,
      default: undefined,  // 'blue' が指定可能
    },
    noBorder: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    // true で色を反転させる (現状 color 指定時のみ)
    active: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: undefined,
    },
    putIconOutside: {
      // アイコンの位置をボックスの外側にする
      type: Boolean,
      default: false,
    },
    iconColor: {
      type: String,
      default: undefined, // red, blue が指定可能
    },
    iconSeparator: {
      type: String,
      default: undefined, // 現状指定可能なのは default のみ
    },
    cursor: {
      type: String,
      default: 'text',
    },

    // -----------------
    // オートコンプリート

    autocompleteItems: {
      type: Array,
      default: () => [],
    },
    maxAutocompleteBoxHeight: {
      type: Number,
      default: 300,
    },

    // ---------------------------------------------------------------------
    // TkTextBox を部品として利用する UI コンポーネントで利用する非公開プロパティ

    // eslint-disable-next-line vue/prop-name-casing
    _extraTextPaddingLeft: {
      type: Number,
      default: 0,
    },
    // eslint-disable-next-line vue/prop-name-casing
    _extraTextPaddingRight: {
      type: Number,
      default: 0,
    },
  },

  styleVars(props) {
    const vars = input.methods.__sizingVars(props.size, props.icon, props.iconSeparator, props.putIconOutside)
    return {
      ...vars,
      '--width': props.width ? `${props.width}px` : '100%',
      '--cursor': props.cursor,
      '--extra-text-padding-left': `${props._extraTextPaddingLeft}px`,
      '--extra-text-padding-right': `${props._extraTextPaddingRight}px`,
      '--view-font-size': Number(vars['--font-size'].slice(0, -2)),  // 'px' の前を切り取る
    }
  },
}
</script>

<style scoped lang="scss">
.TkTextBox {
  position: relative;

  @media (max-width: 657px) {
    height: var(--height);
  }

  &.-disabled {
    opacity: .5;
  }
}

.tkTextBox_input {
  display: inline-flex;
  position: relative;
  width: var(--width);

  // layout
  height: var(--height);
  padding-left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + var(--extra-text-padding-left));
  padding-right: calc(10px + var(--extra-text-padding-right));

  &.-putIconOutside {
    width: calc(var(--width) - var(--outside-icon-space));
    padding-left: calc(var(--text-padding-x) + var(--extra-text-padding-left));
    left: var(--outside-icon-space);
  }

  // theme
  &.-default {
    border: solid 1px #d2d2d2;
    border-radius: 4px;
    box-sizing: border-box;
    box-shadow: inset 2px 2px 2px 2px rgba(51, 51, 51, 0.1);
    background-color: #fff;
  }
  &.-flat {
    border: solid 1px #bababa;
    border-radius: 4px;
    background-color: #fff;
  }

  // color
  &.-blue {
    border-color: #BBD2E6;
    color: #1F6AAD;

    &.-active {
      background-color: #1F6AAD;
      color: #fff;
    }
  }

  &.-noBorder {
    border: unset;
  }

  &.-bold {
    font-weight: bold;
  }

  font-size: var(--font-size);

  @media (max-width: 657px) {
    font-size: 16px;
    --scale-factor: calc(var(--view-font-size) / 16);
    height: calc(var(--height) / var(--scale-factor));
    width: calc(var(--width) / var(--scale-factor));
    transform-origin: top left;
    transform: scale(var(--scale-factor));

    padding-left: calc((var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + var(--extra-text-padding-left)) / var(--scale-factor));
    padding-right: calc((10px + var(--extra-text-padding-right)) / var(--scale-factor));

    &.-putIconOutside {
      width: calc((var(--width) - var(--outside-icon-space)) / var(--scale-factor));
      padding-left: calc((var(--text-padding-x) + var(--extra-text-padding-left)) / var(--scale-factor));
    }
  }

  cursor: var(--cursor);
}

.tkTextBox_icon {
  position: absolute;
  top: var(--icon-padding-y);
  left: var(--icon-padding-x);
  width: var(--icon-size);
  height: var(--icon-size);
  cursor: var(--cursor);

  &.-putIconOutside {
    cursor: auto;
  }
}

.tkTextBox_separator {
  position: absolute;
  z-index: 1;
  cursor: var(--cursor);

  &.-default {
    top: calc(var(--height) * 0.2);
    left: calc(var(--icon-size) + var(--icon-padding-x) * 1.5);
    width: 0.5px;
    height: calc(var(--height) * 0.6);
    background-color: #dfe6e6;
  }
}

.tkTextBox_autocomplete {
  position: absolute;
  z-index: 1;
  width: 100%;
  overflow: auto;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: #fff;
  cursor: pointer;

  &.-default {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.1);
    background-color: #fff;
  }

  &.-putIconOutside {
    width: calc(100% - var(--outside-icon-space));
    left: var(--outside-icon-space);
  }
}

.tkTextBox_autocompleteItem {
  position: relative;
  width: 100%;
  padding: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  // TODO: @media ではなく size プロパティ依存にする
  @media (max-width: 657px) {
    height: 40px;
    font-size: 16px;
    line-height: 28px;
  }

  &.-default {
    &.-border {
      border-bottom: 1px solid #d2d2d2;
    }
    &:hover {
      background-color: #f3f3f3;
    }
  }

  &.-normal {
    height: 28px;
    font-size: 12px;
    line-height: 12px;

    @media (max-width: 657px) {
      height: 32px;
      font-size: 16px;
      line-height: 18px;
    }
  }
  &.-large {
    height: 28px;
    font-size: 12px;
    line-height: 12px;

    @media (max-width: 657px) {
      height: 32px;
      font-size: 16px;
      line-height: 18px;
    }
  }
  &.-x-large {
    height: 36px;
    font-size: 14px;
    line-height: 20px;

    @media (max-width: 657px) {
      height: 36px;
      font-size: 16px;
      line-height: 20px;
    }
  }
}
</style>
